import { useCallback, useState } from 'react'
import * as yup from 'yup'
import _ from 'lodash'
import { useTranslation } from 'react-i18next'
import {
  AGENDA_TASK_BLIND_PUBLICATION,
  AGENDA_TASKS,
  AGENDA_DATE_START,
  AGENDA_DATE_END,
} from 'lib/agenda'

import {
  isStepDisabled,
  SESSIONS_STEPS_LIST,
  SESSIONS_STEP_CAPS,
  SESSIONS_STEP_CARS,
  SESSIONS_STEP_COMPLETED,
  SESSIONS_STEP_COMPLETED_WITH_ERRORS,
  SESSIONS_STEP_CONFIRMATION,
  SESSIONS_STEP_DISABLED,
  SESSIONS_STEP_OPEN,
  SESSIONS_STEP_PLANNING,
  SESSIONS_STEP_PRICING,
  SESSIONS_STEP_SOLD,
} from './sessionsLib'

const useSessionSteps = () => {
  const { t } = useTranslation()

  const [state, setState] = useState({
    session: {},
    carsStepCars: [],
    pricingStepCars: [],
    soldStepCars: [],
    steps: [],
  })

  const handlePlanningValidation = useCallback(
    (session) => {
      const tasksDates = AGENDA_TASKS.filter(
        (task) => task !== AGENDA_TASK_BLIND_PUBLICATION
      ).flatMap((task) => [
        `${task}.${AGENDA_DATE_START}`,
        `${task}.${AGENDA_DATE_END}`,
      ])

      const errors = tasksDates
        .map((taskDate) =>
          yup.date().isValidSync(_.get(session.agenda, taskDate))
            ? null
            : { id: taskDate, error: t('common.date.error.invalidFormat') }
        )
        .filter((v) => v)

      if (errors.length) {
        return errors
      }

      for (let i = 1; i < tasksDates.length; i++) {
        if (
          _.get(session.agenda, tasksDates[i]) <=
          _.get(session.agenda, tasksDates[i - 1])
        ) {
          errors.push({
            id: tasksDates[i],
            error: t('common.date.error.isNotAfter', {
              attribute: t(`sessions.planning.${tasksDates[i - 1]}`),
            }),
          })
          return errors
        }
      }
      return errors
    },
    [t]
  )

  const handleStepValidation = useCallback(
    (step, session) => {
      if (!session) {
        session = state.session
      }
      if (step === SESSIONS_STEP_PLANNING) {
        return handlePlanningValidation(session)
      }
      if (step === SESSIONS_STEP_CARS) {
        return (
          !!session &&
          state.carsStepCars.length &&
          state.carsStepCars.every((car) => car.exists)
        )
      }
      if (step === SESSIONS_STEP_PRICING) {
        return (
          session &&
          state.pricingStepCars.length &&
          state.pricingStepCars.every(
            (car) =>
              car.exists &&
              car.existsInCars &&
              car.existsInPricing &&
              !session.requiredGroups
                .filter((group) => group)
                .some((group) => !car.prices[group])
          )
        )
      }
      if (step === SESSIONS_STEP_SOLD) {
        return (
          session &&
          state.soldStepCars.length &&
          state.soldStepCars.every(
            (car) => car.exists && car.existsInCars && car.soldPartnerName
          )
        )
      }
      if (step === SESSIONS_STEP_CAPS) {
        const validCaps = (session.caps || []).every((cap) => cap.firstChoice)
        const validBlindCaps = (session.blindCaps || []).every(
          (cap) => cap.firstChoice
        )
        return validCaps && validBlindCaps
      }
      if (step === SESSIONS_STEP_CONFIRMATION) {
        return true
      }
      return false
    },
    [
      handlePlanningValidation,
      state.carsStepCars,
      state.pricingStepCars,
      state.session,
      state.soldStepCars,
    ]
  )

  const _isCompletedWithErrors = useCallback(
    (step, state) =>
      step === SESSIONS_STEP_PLANNING
        ? handleStepValidation(step, state.session).length
        : [
            SESSIONS_STEP_CARS,
            SESSIONS_STEP_PRICING,
            SESSIONS_STEP_SOLD,
          ].includes(step)
        ? state[`${step}StepCars`].some(
            (car) => car[`existsIn${step[0].toUpperCase() + step.slice(1)}`]
          ) && !handleStepValidation(step, state.session)
        : step === SESSIONS_STEP_CAPS
        ? (state.session.caps?.some((cap) => cap.firstChoice) ||
            state.session.blindCaps?.some((cap) => cap.firstChoice)) &&
          !handleStepValidation(step, state.session)
        : false,
    [handleStepValidation]
  )

  const getStepsWithState = useCallback(() => {
    if (!Object.keys(state.session).length) return []
    return SESSIONS_STEPS_LIST.map((step) => {
      const result = {
        step,
        label: t(`sessions.${step}.step`),
        state: SESSIONS_STEP_OPEN,
      }
      if (isStepDisabled(state.session, step)) {
        result.state = SESSIONS_STEP_DISABLED
        return result
      }
      if (state.session[`${step}AcceptedAt`]) {
        result.state = SESSIONS_STEP_COMPLETED
        return result
      }

      if (_isCompletedWithErrors(step, state)) {
        result.state = SESSIONS_STEP_COMPLETED_WITH_ERRORS
        return result
      }
      return result
    })
  }, [_isCompletedWithErrors, state, t])

  const init = useCallback((session) => {
    setState((s) => ({
      ...s,
      session,
      carsStepCars: session.cars.filter((car) => car.existsInCars),
      pricingStepCars: session.cars.filter(
        (car) => car.existsInCars || car.existsInPricing
      ),
      soldStepCars: session.cars.filter((car) => car.existsInSold),
    }))
  }, [])

  return {
    carsStepCars: state.carsStepCars,
    pricingStepCars: state.pricingStepCars,
    soldStepCars: state.soldStepCars,
    steps: state.steps,
    initSteps: init,
    handleStepValidation,
    getStepsWithState,
  }
}

export default useSessionSteps
